import dayjs from "dayjs";
import z from "zod";

import { pageSearchParam } from "@/core/hooks/use-list-search";

// the backend rejects a longer search with a 422
export const SESSION_SEARCH_MAX_LENGTH = 64;

// the backend parses every source id as a uuid and rejects the whole request
// for one that is not, so a hand-edited id is dropped here and the rest kept
const sourceIdsParam = z
  .array(z.string())
  .transform((ids) => ids.filter((id) => z.uuid().safeParse(id).success))
  .default([])
  .catch([]);

export const usageSessionsSearchSchema = z.object({
  timeFrame: z
    .enum(["1d", "7d", "30d", "all", "custom"])
    .default("30d")
    .catch("30d"),
  // the custom range's first and last day, inclusive
  from: z.iso.date().optional().catch(undefined),
  to: z.iso.date().optional().catch(undefined),
  // only the superuser list reads it; org scopes take their org from the path
  orgId: z.string().optional().catch(undefined),
  userId: z.string().optional().catch(undefined),
  session: z.string().max(SESSION_SEARCH_MAX_LENGTH).default("").catch(""),
  datasourceIds: sourceIdsParam,
  collectionIds: sourceIdsParam,
  page: pageSearchParam,
});

export type UsageSessionsSearch = z.infer<typeof usageSessionsSearchSchema>;

// superuser sessions span every organization, so their source filters wait
// for an organization to be picked
export type UsageSessionsScope =
  | { kind: "superuser" }
  | { kind: "organization"; orgId: string; departmentId?: string };

/** the organization whose sources the list can be narrowed to, if any */
export function sourceOrgIdFor(
  scope: UsageSessionsScope,
  search: Pick<UsageSessionsSearch, "orgId">,
): string | undefined {
  return scope.kind === "organization" ? scope.orgId : search.orgId;
}

type PresetTimeFrame = Exclude<
  UsageSessionsSearch["timeFrame"],
  "all" | "custom"
>;

const TIME_FRAME_DAYS: Record<PresetTimeFrame, number> = {
  "1d": 1,
  "7d": 7,
  "30d": 30,
};

/** the picked custom range, or null while it is incomplete or reversed */
export function customRange(
  search: Pick<UsageSessionsSearch, "timeFrame" | "from" | "to">,
): { from: string; to: string } | null {
  const { timeFrame, from, to } = search;
  if (timeFrame !== "custom" || !from || !to || from > to) return null;
  return { from, to };
}

/** the start of a preset time frame, counted back from now */
export function presetFromTimestamp(
  timeFrame: UsageSessionsSearch["timeFrame"],
): string | undefined {
  if (timeFrame === "all" || timeFrame === "custom") return undefined;
  return dayjs().subtract(TIME_FRAME_DAYS[timeFrame], "day").toISOString();
}

/**
 * The list endpoints' filter params for the url state. source ids only apply
 * while ``sourceOrgId`` is set, since their controls are hidden otherwise.
 */
export function sessionsFilterQuery(
  search: UsageSessionsSearch,
  {
    sourceOrgId,
    presetFrom,
  }: { sourceOrgId: string | undefined; presetFrom: string | undefined },
) {
  const range = customRange(search);
  const sourceIds = (ids: string[]) =>
    sourceOrgId && ids.length > 0 ? ids : undefined;

  let fromTimestamp = search.timeFrame === "custom" ? undefined : presetFrom;
  let toTimestamp: string | undefined;
  if (range) {
    fromTimestamp = dayjs(range.from).startOf("day").toISOString();
    toTimestamp = dayjs(range.to).endOf("day").toISOString();
  }

  return {
    from_timestamp: fromTimestamp,
    to_timestamp: toTimestamp,
    datasource_ids: sourceIds(search.datasourceIds),
    collection_ids: sourceIds(search.collectionIds),
    user_id: search.userId,
    session_search: search.session.trim() || undefined,
  };
}
